import axios, { AxiosError, type AxiosInstance } from 'axios'

/**
 * Axios API 客户端
 *
 * - 自动从 localStorage 注入 JWT
 * - 401 时清除 token 并跳转登录
 * - 统一错误格式：{ statusCode, message, code, details }
 */

const STORAGE_KEY = 'license-admin-token'

/** API 基址：开发期留空（走 Vite proxy），生产期填完整域名 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? ''

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' }
})

// ── 请求拦截：注入 JWT ────────────────────────────────────
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(STORAGE_KEY)
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

/** 后端错误响应格式 */
export interface ApiError {
  statusCode: number
  code: string
  message: string
  details?: unknown
}

/** 统一错误归一化 */
export function normalizeError(err: unknown): ApiError {
  const axiosErr = err as AxiosError<{ error?: { code?: string; message?: string; details?: unknown } }>
  if (axiosErr.response?.data?.error) {
    return {
      statusCode: axiosErr.response.status,
      code: axiosErr.response.data.error.code ?? 'UNKNOWN',
      message: axiosErr.response.data.error.message ?? '请求失败',
      details: axiosErr.response.data.error.details
    }
  }
  if (axiosErr.response) {
    return {
      statusCode: axiosErr.response.status,
      code: 'HTTP_ERROR',
      message: `HTTP ${axiosErr.response.status}`
    }
  }
  return {
    statusCode: 0,
    code: 'NETWORK_ERROR',
    message: axiosErr.message ?? '网络错误'
  }
}

// ── 响应拦截：401 自动登出 ────────────────────────────────
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const normalized = normalizeError(error)
    if (normalized.statusCode === 401) {
      localStorage.removeItem(STORAGE_KEY)
      // 不在此处跳转，由 authProvider 的 check 钩子处理
    }
    return Promise.reject(normalized)
  }
)

// ── Token 管理工具 ────────────────────────────────────────
export const tokenStorage = {
  get(): string | null {
    return localStorage.getItem(STORAGE_KEY)
  },
  set(token: string): void {
    localStorage.setItem(STORAGE_KEY, token)
  },
  clear(): void {
    localStorage.removeItem(STORAGE_KEY)
  }
}

export type { AxiosInstance }
